import React from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
// eslint-disable-next-line
import { css } from "styled-components/macro";

import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";

import logo from "../../images/logos/logosafetylocks.jpg";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";

const Header = tw.header`
  flex max-w-screen-xl mx-auto
`;

const InfoChip = styled.span`
  ${tw`flex items-center px-4 py-1 rounded-full border font-medium text-green-700 border-green-500 bg-white shadow-sm transition duration-200`}
  svg {
    ${tw`w-4 h-4 mr-1 text-green-600`}
  }

  &:hover {
    ${tw`bg-green-500 border-green-600`}
  }
`;

const SlantedBackground2 = styled.span`
  ${tw`relative text-green-700 px-4 -mx-4 py-2`}
  &::before {
    content: "";
    ${tw`absolute inset-0 bg-green-200 transform -skew-x-12 -z-10`}
  }`;

export const NavLinks = tw.div`inline-block`;

export const NavLink = tw.a`
  text-lg my-2 lg:text-sm xl:mx-6 lg:mx-3 lg:my-0 mx-0
  font-semibold tracking-wide transition duration-300
  pb-1 border-b-2 border-transparent hover:border-primary-500 hocus:text-primary-500
`;

export const PrimaryLink = tw.a`
  px-6 py-3 rounded-full bg-primary-500 text-gray-100
  hocus:bg-primary-700 focus:shadow-outline
  text-lg my-2 lg:text-sm mx-0 lg:my-0
  font-semibold tracking-wide transition duration-300
  border-b-2 border-transparent hover:border-primary-500
`;

export const LogoLink = styled(NavLink)`
  ${tw`flex font-black border-b-0 text-2xl! ml-0!`};

  img {
    ${tw`w-32 h-auto`}
  }
`;

export const MobileNavLinksContainer = tw.nav`flex flex-1 items-center justify-between`;
export const NavToggle = tw.button`
  lg:hidden z-20 focus:outline-none hocus:text-primary-500 transition duration-300
`;
export const MobileNavLinks = motion(styled.div`
  ${tw`lg:hidden z-10 fixed top-0 inset-x-0 mx-4 my-16 p-8 border text-center rounded-lg text-gray-900 bg-white`}
  ${NavLinks} {
    ${tw`flex flex-col items-center`}
  }
`);

export const DesktopNavLinks = tw.nav`
  hidden lg:flex flex-1 justify-between items-center
`;

export default ({ roundedHeaderButton = false, logoLink, links, className, collapseBreakpointClass = "lg" }) => {
  const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();

  const defaultLinks = [
    <NavLinks key={1}>
      <NavLink onClick={toggleNavbar} href="/#diensten">Diensten</NavLink>
      <NavLink onClick={toggleNavbar} href="/#aanbiedingen">Aanbiedingen</NavLink>
      <NavLink onClick={toggleNavbar} href="/#faq">Vragen</NavLink>
      <NavLink onClick={toggleNavbar} href="/zakelijk">Zakelijk</NavLink>
      <NavLink onClick={toggleNavbar} href="/blogs">Blogs</NavLink>
      <NavLink onClick={toggleNavbar} href="/#contact">Contact</NavLink>
      <PrimaryLink css={roundedHeaderButton && tw`rounded-full`}href="/offerte-aanvragen">Offerte</PrimaryLink>
    </NavLinks>
  ];

  const collapseBreakpointCss = collapseBreakPointCssMap[collapseBreakpointClass];

  const defaultLogoLink = (
      <LogoLink href="/">
        <img src={logo} alt="logo" className={"w-56 h-10"} />
      </LogoLink>
  );

  logoLink = logoLink || defaultLogoLink;
  links = links || defaultLinks;

  return (
      <div className="">
        <div className="flex w-full justify-center md:gap-[175px] lg:gap-[200px] xl:gap-[400px] sm:gap-[125px] gap-9 md:text-base sm:text-sm text-xs text-green-600 mb-2">
          {/* --- TOP INFO BAR --- */}
        <div className="flex flex-wrap justify-center gap-3 mb-3 text-sm">
          <InfoChip>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Gratis offertes
          </InfoChip>

          <InfoChip>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Meerpuntssluitingen plaatsen
          </InfoChip>

          <InfoChip>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            GEEN voorrijkosten
          </InfoChip>
        </div>
        </div>
        <Header className={className || "header-light"}>
          <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
            {logoLink}
            {links}
          </DesktopNavLinks>

          <MobileNavLinksContainer css={collapseBreakpointCss.mobileNavLinksContainer}>
            {logoLink}
            <MobileNavLinks initial={{x: "150%", display: "none"}} animate={animation}
                            css={collapseBreakpointCss.mobileNavLinks}>
              {links}
            </MobileNavLinks>
            <NavToggle onClick={toggleNavbar} className={showNavLinks ? "open" : "closed"}>
              {showNavLinks ? <CloseIcon tw="w-6 h-6"/> : <MenuIcon tw="w-6 h-6"/>}
            </NavToggle>
          </MobileNavLinksContainer>
        </Header>
      </div>
  );
};

const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden`
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden`
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`
  },
  xl: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`
  }
};
